import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  deriveLibraryTitle,
  getLibraryProviderLabel,
  type LibraryDuplicateWarning,
} from "@/lib/library";
import type { LibraryItemWithCategories } from "@/lib/libraryCategories";

type LibraryRecord = LibraryItemWithCategories<{ id: string }>;

type Props = {
  warnings: LibraryDuplicateWarning[];
  /** Used to surface category names for matched items. Pass the full library list. */
  items: Array<LibraryRecord & { id: string }>;
  /** Called when the user clicks "Open existing record" — should also close the duplicate dialog. */
  onOpenExisting: (itemId: string) => void;
};

export const LibraryDuplicateWarnings = ({ warnings, items, onOpenExisting }: Props) => (
  <div className="space-y-3">
    {warnings.map((warning) => {
      const matchingItem = items.find((item) => item.id === warning.item.id);
      const categoryNames = matchingItem?.categories.map((category) => category.name).join(" · ") ?? "";

      return (
        <div key={warning.item.id} className="rounded-md border border-border bg-muted/20 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-medium text-foreground">{deriveLibraryTitle(warning.item)}</p>
              <p className="text-sm text-muted-foreground">{warning.item.author_text || "Unknown author"}</p>
            </div>
            <Badge variant={warning.severity === "strong" ? "secondary" : "outline"}>
              {warning.severity === "strong" ? "Likely duplicate" : "Possible match"}
            </Badge>
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
            <Badge variant="outline">{getLibraryProviderLabel(warning.item.source_provider)}</Badge>
            {warning.item.published_year ? <Badge variant="outline">{warning.item.published_year}</Badge> : null}
            {warning.item.edition ? <Badge variant="outline">{warning.item.edition}</Badge> : null}
            {warning.item.volume ? <Badge variant="outline">Vol. {warning.item.volume}</Badge> : null}
            {warning.item.isbn_13 ? (
              <Badge variant="outline">ISBN-13 {warning.item.isbn_13}</Badge>
            ) : warning.item.isbn_10 ? (
              <Badge variant="outline">ISBN-10 {warning.item.isbn_10}</Badge>
            ) : null}
          </div>
          {categoryNames ? <p className="mt-2 text-xs text-muted-foreground">Categories: {categoryNames}</p> : null}
          <p className="mt-2 text-sm text-muted-foreground">{warning.detail}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenExisting(warning.item.id)}>
              Open existing record
            </Button>
          </div>
        </div>
      );
    })}
  </div>
);
